import React, { useState, useEffect, useRef, useMemo } from "react";
import topTost from "../../../utils/topTost";
import Dropdown from "../Dropdown";
import { useLocation } from "react-router-dom";
import { DISABLE_COLUMN_VISIBILITY_FOR } from "./tableConfig";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";

const TableSearch = ({
  table,
  setGlobalFilter,
  globalFilter,
  setColumnVisibility,
  pdfTitle,
   pageSize,
  setPageSize
}) => {
  const effectivePageSize = pageSize ?? table.getState().pagination.pageSize;
  // const [selectAll, setSelectAll] = useState(false);
  const dropdownRef = useRef();
  const location = useLocation();
  const [pdfColumns, setPdfColumns] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);

  const disableColumnVisibility =
    DISABLE_COLUMN_VISIBILITY_FOR[location.pathname] ?? false;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Optional: close dropdown if needed
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleableColumns = useMemo(() => {
    return table
      .getAllLeafColumns()
      .filter((col) => table.getColumn(col.id)?.getCanHide())
      .map((col) => ({
        ...col,
        normalizedId: col.id.trim().toLowerCase(),
        isVisible: table.getColumn(col.id)?.getIsVisible(),
      }));
  }, [table.getState().columnVisibility]);

  const copyToClipboard = () => {
    const columns = table
      .getVisibleLeafColumns()
      .filter((column) => column.id.toLowerCase() !== "actions");
    const headers = columns.map((column) =>
      typeof column.columnDef.header === "function"
        ? column.columnDef.header()
        : column.columnDef.header
    );

    const rows = table.getRowModel().rows.map((row) =>
      row
        .getVisibleCells()
        .filter((cell) => cell.column.id.toLowerCase() !== "actions")
        .map((cell) => cell.getValue())
    );

    const content =
      "Exported Table\n\n" +
      [headers, ...rows].map((row) => row.join("\t")).join("\n");

    navigator.clipboard.writeText(content).then(() => {
      topTost({ title: "Copied to clipboard!", icon: "success" });
    });
  };

  const allVisible = toggleableColumns.every((col) => col.isVisible);

  const columnItems = [
    {
      id: "select_all",
      label: allVisible ? "Select All" : "Deselect All",
      checked: allVisible,
      checkbox: true,
      isSelectAll: true,
    },
    ...toggleableColumns.map((col) => {
      const header = col.columnDef.header;
      return {
        id: col.id,
        label: typeof header === "function" ? header() : header,
        checked: col.isVisible,
        checkbox: true,
      };
    }),
  ];

  const handleCheckboxChange = (columnId) => {
    if (columnId === "select_all") {
      const allVisible = toggleableColumns.every((col) => col.getIsVisible());
      const newVisibility = !allVisible;

      const updatedVisibility = toggleableColumns.reduce((acc, col) => {
        acc[col.id] = newVisibility;
        return acc;
      }, {});

      setColumnVisibility(updatedVisibility);
    } else {
      setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: !(prev?.[columnId] ?? true),
      }));
    }
  };

  const exportToPDF = (selectedColumnIds) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth(); // ADD THIS

    /* ---------- TITLE ---------- */
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(pdfTitle || "Exported Data", pageWidth / 2, 15, {
      align: "center",
    });

    const currentPath = location.pathname.split("/").pop();
    const formattedPageName = currentPath
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

    const now = new Date();
    const formattedDateTime = now
      .toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/[/,]/g, "-")
      .replace(/\s+/g, "_");

    const fileName = `${formattedPageName}_${formattedDateTime}.pdf`;

    const selectedColumns = table
      .getAllLeafColumns()
      .filter(
        (col) =>
          selectedColumnIds.includes(col.id) &&
          col.id.toLowerCase() !== "actions"
      );

    const formatDateTime = (value) => {
      if (!value) return "";

      const date = new Date(value);

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    const headers = [
      "S.NO",
      ...selectedColumns.map((col) => {
        const header =
          typeof col.columnDef.header === "function"
            ? col.columnDef.header()
            : col.columnDef.header;
        return String(header).toUpperCase();
      }),
    ];

    const rows = table.getRowModel().rows.map((row, index) => {
      const rowData = selectedColumns.map((col) => {
        const cell = row.getAllCells().find((c) => c.column.id === col.id);
        const value = cell?.getValue();

        if (
          value &&
          typeof value === "string" &&
          value.includes("T") &&
          value.includes("Z")
        ) {
          return formatDateTime(value);
        }

        return typeof value === "string" ? value.toUpperCase() : value;
      });
      return [index + 1, ...rowData];
    });

    autoTable(doc, {
      startY: 30,
      head: [headers],
      body: rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
    });

    doc.save(fileName);
  };

  const exportToExcel = () => {
    const excelTitle = pdfTitle || "Exported Data"; // Your title prop or fallback

    // Get columns and headers
    const columns = table
      .getVisibleLeafColumns()
      .filter((col) => col.id.toLowerCase() !== "actions");

    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "000000" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      fill: {
        fgColor: { rgb: "E9EEF3" }, // soft grey-blue
      },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    };

    const headers = columns.map((col) => {
      let header =
        typeof col.columnDef.header === "function"
          ? col.columnDef.header()
          : col.columnDef.header;

      return String(header).toUpperCase();
    });

    const formatDateTime = (value) => {
      if (!value) return "";

      const date = new Date(value);

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    const STATUS_TEXT = {
      0: "Pending",
      1: "Approved",
      2: "Rejected",
      3: "On Hold",
      4: "Block",
    };
    // Get data rows
    const rows = table.getRowModel().rows.map((row) =>
      row
        .getVisibleCells()
        .filter((cell) => cell.column.id.toLowerCase() !== "actions")

        .map((cell) => {
          let value = cell.getValue();
          if (cell.column.id === "payment_status") {
            return value === 1 ? "Yes" : "No";
          }
          if (cell.column.id === "otp_verify") {
            return value === 1 ? "yes" : "No";
          }
          if (cell.column.id === "status") {
            return STATUS_TEXT[value] ?? value;
          }
          // Check if value is date-like
          if (
            typeof value === "string" &&
            value.includes("T") &&
            value.includes("Z")
          ) {
            return formatDateTime(value);
          }
          return value;
        })
    );

    // Create worksheet data with title, blank row, headers, and rows
    const worksheetData = [[excelTitle], [], headers, ...rows];

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Header row index = 2 (because row 0 = title, row 1 = blank)
    headers.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 2, c: colIndex });

      if (!worksheet[cellAddress]) return;

      worksheet[cellAddress].s = headerStyle;
    });

    // Merge first row cells across the width of the table
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // Dynamically set column widths based on content
    worksheet["!cols"] = headers.map((header, colIndex) => {
      const columnValues = [header, ...rows.map((row) => row[colIndex] || "")];

      const maxLength = Math.max(
        ...columnValues.map((val) => val.toString().length)
      );
      return { wch: maxLength + 5 };
    });

    // Optionally style the title cell (bold, centered)
    if (!worksheet["A1"].s) worksheet["A1"].s = {};
    worksheet["A1"].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      alignment: { horizontal: "center", vertical: "center" },
      fill: {
        fgColor: { rgb: "E9EEF3" }, // same soft background
      },
    };

    // Create workbook and append worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Export");

    // Save file with a friendly name
    XLSX.writeFile(workbook, `${excelTitle}.xlsx`);
  };

  return (
    <>
      <div className="row gy-2 position-relative">
        <div className="col-sm-12 col-md-6 ps-0 m-0 pb-10">
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-md-start justify-content-center">
            {!disableColumnVisibility && (
              <div className="d-flex gap-2 position-relative" ref={dropdownRef}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={copyToClipboard}
                >
                  Copy
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowPdfModal(true)}
                >
                  PDF
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={exportToExcel}
                >
                  Excel
                </button>
                <Dropdown
                  dropdownItems={columnItems}
                  triggerIcon={
                    <span className="fw-semibold">Column Visibility</span>
                  }
                  triggerPosition="0,22"
                  triggerClass="btn btn-outline-secondary btn-sm"
                  dropdownAutoClose="outside"
                  tooltipTitle="Toggle Columns"
                  onCheckboxChange={handleCheckboxChange}
                  isAvatar={false}
                  disableColumnVisibility={disableColumnVisibility}
                />
              </div>
            )}
            <div className="dataTables_length d-flex align-items-center">
              <label className="d-flex align-items-center gap-2 m-0 m-1">
                Show
                <select
                  className="form-select form-select-sm w-auto pe-4"
                  value={effectivePageSize}
                  onChange={(e) => {
                    if (pageSize) setPageSize(Number(e.target.value));
                    else table.setPageSize(Number(e.target.value));
                  }}
                >
                  {[10, 20, 50, 100, 200, 500, 1000, 1500, 2000].map(
                    (size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    )
                  )}
                </select>
                entries
              </label>
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-6 ps-0 m-0 pb-10">
          <div className="dataTables_filter d-flex justify-content-md-end justify-content-center">
            <label className="d-inline-flex align-items-center gap-2 m-0">
              Search:
              <input
                type="text"
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search..."
                className="form-control form-control-sm"
              />
            </label>
          </div>
        </div>
      </div>

      {showPdfModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{ backdropFilter: "blur(4px)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Columns for PDF</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPdfModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {table
                  .getAllLeafColumns()
                  .filter((col) => col.columnDef?.meta?.exportable !== false)
                  .map((col) => {
                    const header =
                      typeof col.columnDef.header === "function"
                        ? col.columnDef.header().toUpperCase()
                        : col.columnDef.header.toUpperCase();
                    return (
                      <div key={col.id} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`pdf-col-${col.id}`}
                          checked={pdfColumns.includes(col.id)}
                          onChange={() =>
                            setPdfColumns((prev) =>
                              prev.includes(col.id)
                                ? prev.filter((id) => id !== col.id)
                                : [...prev, col.id]
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`pdf-col-${col.id}`}
                        >
                          {header}
                        </label>
                      </div>
                    );
                  })}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPdfModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    exportToPDF(pdfColumns);
                    setShowPdfModal(false);
                  }}
                >
                  Export PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TableSearch;
